import { logger } from '@sentry/utils'
import { Flex, List, Modal, notification, Spin } from 'antd'
import { groupBy } from 'lodash-es'
import { FunctionComponent, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDeleteKolContactInfoMutation } from '@/api/workspace-api/directory-api'
import Actions from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/actions'
import ContactInfoModal from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/contact-info-modal'
import Header from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/header'
import { useKolContactInfo } from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/use-kol-contact-info'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  ContactInfo,
  ContactType,
  contactTypeI18nMap,
  contactTypeSchema,
} from '@/types/schema/directory/kol-management'

interface ContactInfoProps {
  kolId: string
  isFetching: boolean
  responseData?: ContactInfo[]
}

const mockEmptyType: Record<
  Extract<ContactType, 'email' | 'phone'>,
  ContactInfo
> = {
  email: {
    directory_contact_id: -1,
    type: 'email',
    resource_type: 'directory',
    value: '',
  },
  phone: {
    directory_contact_id: -1,
    type: 'phone',
    value: '',
  },
}

const ContactInfoForm: FunctionComponent<ContactInfoProps> = ({
  kolId,
  isFetching,
  responseData,
}) => {
  const { formatMessage } = useIntl()
  const [contact, setContact] = useState<ContactInfo>()
  const [openModal, setOpenModal] = useState(false)
  const { handleEditContactEvent } = useKolContactInfo(kolId)
  const [deleteContactInfo] = useDeleteKolContactInfoMutation()

  const groupedContactList: Partial<Record<ContactType, ContactInfo[]>> =
    useMemo(() => {
      const contacts = responseData ? [...responseData] : []

      Object.entries(mockEmptyType).forEach(([type, info]) => {
        if (!contacts.some((item) => item.type === type)) {
          contacts.push(info)
        }
      })

      const contactTypeOrder = contactTypeSchema.options
      contacts.sort(
        (a, b) =>
          contactTypeOrder.indexOf(a.type) - contactTypeOrder.indexOf(b.type),
      )

      return groupBy(contacts, 'type')
    }, [responseData])

  const handleClosed = (): void => {
    setContact(undefined)
    setOpenModal(false)
  }

  const handleEdit = (value: ContactInfo): void => {
    setContact(value)
    setOpenModal(true)
  }

  const handleDelete = (id: number, type: ContactType): void => {
    Modal.confirm({
      centered: true,
      title: formatMessage({ id: 'general:confirm_delete_title' }),
      content: formatMessage({ id: 'general:confirm_delete_description' }),
      cancelText: formatMessage({ id: 'general:btn_text_cancel' }),
      okText: formatMessage({ id: 'general:btn_text_confirm' }),
      onOk: async () => {
        try {
          await deleteContactInfo({ kolId, contactInfoId: id }).unwrap()
          handleEditContactEvent(type, true)
        } catch (e) {
          logger.error(e)
          notification.error({
            message: formatMessage({ id: 'general:operation_failure' }),
            placement: 'bottomRight',
          })
        }
      },
    })
  }

  const renderItem = (title: string, item: ContactInfo): ReactNode => {
    const { value, notes, resource_type, type } = item

    const parsedValue = ((): string => {
      if (type === 'email' && resource_type === 'fb') {
        return `${value} (${formatMessage({ id: 'irm:public' })})`
      }

      if (type === 'email' && notes) {
        return `${value} (${notes})`
      }

      return value
    })()

    return (
      <ItemContent>
        <Flex gap={40} justify='center'>
          <Title>{title}</Title>
          <Value>{parsedValue}</Value>
        </Flex>
        <Actions
          onDelete={
            item.directory_contact_id > 0
              ? (): void => handleDelete(item.directory_contact_id, item.type)
              : undefined
          }
          onEdit={
            item.directory_contact_id > 0
              ? (): void => handleEdit(item)
              : undefined
          }
        />
      </ItemContent>
    )
  }

  return (
    <>
      <Spin spinning={isFetching}>
        <Header
          titleI18nId='kol_cooperation_detail_drawer:tabs_contact_info'
          onClickAddButton={(): void => setOpenModal(true)}
        />
        {Object.entries(groupedContactList).map(([type, contacts]) => {
          const title = formatMessage({ id: contactTypeI18nMap[type] })
          return (
            <Container key={type}>
              <List
                dataSource={contacts}
                itemLayout='horizontal'
                renderItem={(item) => renderItem(title, item)}
              />
            </Container>
          )
        })}
      </Spin>

      <ContactInfoModal
        contact={contact}
        kolId={kolId}
        open={openModal}
        onClose={handleClosed}
      />
    </>
  )
}

export default ContactInfoForm

const Title = styled.div`
  min-width: 80px;
`

const Value = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  :hover {
    overflow: visible;
    word-break: break-word;
    white-space: pre-wrap;
  }
`

const ItemContent = styled(List.Item)`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  &:hover > :last-child {
    visibility: visible;
  }
`

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  font-size: 14px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};

  border-bottom: ${({ theme }): string =>
    `1px solid ${theme.colors.border.divider}`};

  > .ant-list {
    width: 100%;
  }
`
